import { memo, useCallback } from "react";
import { type EscalationRiskTier, EscalationRiskTierSchema } from "@remi/enums/escalation-risk-tier";
import { type EscalationStage, EscalationStageSchema } from "@remi/enums/escalation-stage";
import { EscrowStatusDisplayNames } from "@remi/enums/escrow-status";
import { FinancingStatusDisplayNames } from "@remi/enums/financing-status";
import { FinancingVendorDisplayNames } from "@remi/enums/financing-vendor";
import { NotificationType } from "@remi/enums/notification/type";
import { OrganizationType } from "@remi/enums/organization-type";
import { TicketHistoryField } from "@remi/enums/ticket/history/field";
import { TicketPriorityDisplayNames } from "@remi/enums/ticket/priority";
import { TicketStatus, TicketStatusDisplayNames } from "@remi/enums/ticket/status";
import { formatDistanceToNowShort, stripMarkdown } from "@remi/formatting";
import {
  RnAt,
  RnCancel,
  RnDollarBill,
  RnDollarSign,
  RnDot,
  RnForm,
  RnMessage,
  RnPriorityHigh,
  RnPriorityLow,
  RnPriorityMed,
  RnPriorityUrgent,
  RnReply,
  RnSnooze,
  RnStatusDone,
  RnStatusInProgress,
  RnStatusNotStarted,
  RnTriage,
  RnUserPencil,
} from "@remi/icons/next";
import { RemiCheckbox } from "@remi/next-ui";
import { cn } from "@remi/next-ui/utils";
import type { NotificationListItem } from "@remi/types/notification/list";
import type { ConversationItemNotificationType } from "@remi/types/notification/type";
import {
  EscalationRiskTierDisplayNames,
  EscalationStageDisplayNames,
  isBackwardTransition,
} from "@remi/types-enums/escalation";
import { PandadocDocumentStatusDisplayNames } from "@remi/types-enums/pandadocDocumentStatus";

import { blurActiveCheckbox } from "~/utils/blurActiveCheckbox";
import { SingleAvatar } from "~/components/person/PersonAvatar";
import { Emoji } from "~/components/ui/Emoji";
import { useUserProfile } from "~/contexts/UserProfileContext";

type User = ReturnType<typeof useUserProfile>["user"];

type NotificationItemProps = {
  index: number;
  notification: NotificationListItem;
  groupedNotifications?: NotificationListItem[];
  selected: boolean;
  isActive?: boolean;
  isToast?: boolean;
  // List selection (omitted in toast mode):
  showCheckbox?: boolean;
  // Mobile select mode: force the checkbox visible (no hover on touch).
  selectMode?: boolean;
  onToggle?: () => void;
  onShiftSelect?: () => void;
  onRowClick?: () => void;
  // Toast drag-vs-tap detection (omitted in list mode):
  onPointerDown?: (event: React.PointerEvent<HTMLDivElement>) => void;
  onPointerUp?: (event: React.PointerEvent<HTMLDivElement>) => void;
};

export const NotificationItem = memo(
  ({
    index,
    notification,
    groupedNotifications = [],
    selected,
    isActive = false,
    isToast = false,
    showCheckbox = false,
    selectMode = false,
    onToggle,
    onShiftSelect,
    onRowClick,
    onPointerDown,
    onPointerUp,
  }: NotificationItemProps) => {
    const { user } = useUserProfile();
    const { title, subtitle, icon } = getNotificationDisplayInformation(notification, user);
    const timestamp = getTimestamp(notification);
    const bid = getBidName(notification);

    const totalNotifications = groupedNotifications.length + 1;

    const handleContextMenu = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
      // Only prevent context menu on touch devices (mobile long-press)
      // Allow right-click menu on desktop
      if (e.nativeEvent instanceof PointerEvent && e.nativeEvent.pointerType === "touch") {
        e.preventDefault();
      }
    }, []);

    return (
      <div
        className={cn(
          "group z-0 flex h-[80px] min-h-[80px] w-full min-w-0 max-w-full cursor-pointer select-none flex-col items-start gap-1 overflow-hidden border-border-tertiary border-t border-b px-3 py-2 text-xs transition-all [-webkit-touch-callout:none] hover:bg-background-primaryHover focus:outline-hidden",
          index !== 0 ? "mt-[-1px]" : "border-t-background-primary",
          (selected || isActive) && "!bg-background-accentLight z-10 border-border-accent border-t-border-accent",
        )}
        role="button"
        tabIndex={0}
        aria-current={isActive ? "true" : undefined}
        onClick={onRowClick}
        onKeyDown={(e) => {
          // role="button" on a div isn't keyboard-activated by the browser — wire it up.
          // Shift+Enter mirrors shift+click (range select); Enter/Space is the primary action.
          if (e.key === "Enter" && e.shiftKey && onShiftSelect) {
            e.preventDefault();
            onShiftSelect();
          } else if (onRowClick && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            onRowClick();
          }
        }}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onContextMenu={handleContextMenu}
      >
        <div className="flex w-full min-w-0 max-w-full flex-row items-center gap-2 overflow-hidden">
          <div className="flex min-w-0 flex-1 items-center gap-1">
            {!notification.readAt && !isToast && <RnDot className="h-2 w-2 min-w-2 text-icon-accent" />}
            {bid && <span className="truncate text-text-secondary text-xs">{bid}</span>}
          </div>
          {totalNotifications > 1 && (
            <div
              className={cn(
                "flex h-4 min-w-6 items-center justify-center rounded-full bg-background-accentMedium px-1.5",
                isToast && "mr-1",
              )}
            >
              <span className="font-medium text-text-accent text-xs">
                {totalNotifications < 100 ? totalNotifications : "99+"}
              </span>
            </div>
          )}
        </div>
        <div className="flex w-full flex-row items-center gap-2">
          <div className="flex items-center">
            {showCheckbox ? (
              // Fixed 36px (size-9 = xlg avatar) frame so swapping the avatar for the
              // checkbox on hover/focus/selection never shifts the row text.
              <div className="relative size-9 shrink-0">
                <div
                  className={cn(
                    "absolute inset-0 transition-opacity duration-200 ease-out",
                    selected || selectMode
                      ? "pointer-events-none opacity-0"
                      : "opacity-100 group-focus-within:pointer-events-none group-focus-within:opacity-0 group-hover:pointer-events-none group-hover:opacity-0",
                  )}
                >
                  <SingleAvatar size="xlg" personId={notification.actor.id} />
                  <div className="absolute right-0 bottom-0 flex h-4 w-4 items-center justify-center rounded-full bg-background-primary">
                    <div className="h-3 w-3 rounded-full bg-background-secondary text-icon-secondary">{icon}</div>
                  </div>
                </div>
                <div
                  className={cn(
                    "absolute inset-0 flex cursor-pointer items-center justify-center transition-opacity duration-200 ease-out",
                    selected || selectMode
                      ? "opacity-100"
                      : "pointer-events-none opacity-0 group-focus-within:pointer-events-auto group-focus-within:opacity-100 group-hover:pointer-events-auto group-hover:opacity-100",
                  )}
                  // The whole 36px frame toggles selection (and stops the click from
                  // reaching the row, which would navigate + mark read). Without this,
                  // a click landing in the margin around the 16px checkbox falls
                  // through to the row and the notification vanishes from the list.
                  onClick={(e) => {
                    e.stopPropagation();
                    if (e.shiftKey) {
                      onShiftSelect?.();
                    } else {
                      onToggle?.();
                    }
                    // Mouse clicks: blur the focused checkbox so :focus-within doesn't
                    // keep it visible after deselect. detail === 0 is keyboard — keep focus.
                    if (e.detail !== 0) {
                      blurActiveCheckbox();
                    }
                  }}
                >
                  <RemiCheckbox
                    size="md"
                    checked={selected}
                    aria-label={`${selected ? "Deselect" : "Select"} notification: ${title}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (e.shiftKey) {
                        e.preventDefault();
                        onShiftSelect?.();
                      }
                      if (e.detail !== 0) {
                        blurActiveCheckbox();
                      }
                    }}
                    onCheckedChange={() => onToggle?.()}
                  />
                </div>
              </div>
            ) : (
              <div className="relative">
                <SingleAvatar size="xlg" personId={notification.actor.id} />
                <div className="absolute right-0 bottom-0 flex h-4 w-4 items-center justify-center rounded-full bg-background-primary">
                  <div className="h-3 w-3 rounded-full bg-background-secondary text-icon-secondary">{icon}</div>
                </div>
              </div>
            )}
          </div>
          <div className="flex min-w-0 flex-1 flex-col justify-between">
            <div className="flex w-full items-center justify-between gap-1">
              <span className="truncate text-nowrap font-medium text-text-primary text-xs">{title}</span>
            </div>
            <div className="flex w-full items-center justify-between gap-1">
              <span className="truncate text-nowrap font-normal text-text-secondary text-xs">{subtitle}</span>
              <span className="text-nowrap font-normal text-text-tertiary text-xs">{timestamp}</span>
            </div>
          </div>
        </div>
      </div>
    );
  },
);
NotificationItem.displayName = "NotificationItem";

const getConversationName = (
  conversation: Exclude<NotificationListItem["conversationItem"], undefined>["conversation"],
  user: User,
): string => {
  const isRemi = user.person.organization.type === OrganizationType.REMI_INTERNAL;
  if (conversation.ticket) {
    return `on "${conversation.ticket.title}"`;
  } else if (conversation.escalation) {
    return `on ${conversation.escalation.name}'s escalation`;
  } else if (conversation.bid) {
    return isRemi ? `in ${conversation.name} on ${conversation.bid.name}` : `on ${conversation.bid.name}'s project`;
  }
  return "";
};

const getTicketName = (ticket: { title: string }): string => {
  return `on "${ticket.title}"`;
};

const getBidName = (notification: NotificationListItem): string | undefined => {
  if (notification.ticketHistory) {
    return notification.ticketHistory.ticket.bid?.name;
  } else if (notification.conversationItem) {
    return (
      notification.conversationItem.conversation.bid?.name ??
      notification.conversationItem.conversation.ticket?.bid?.name
    );
  } else if (notification.escrowHistory) {
    return notification.escrowHistory.bid.name;
  } else if (notification.financingHistory) {
    return notification.financingHistory.bid.name;
  } else if (notification.escalationHistory) {
    return notification.escalationHistory.homeownerName ?? undefined;
  } else if (notification.pandadocDocumentHistory) {
    return notification.pandadocDocumentHistory.bid.name;
  }
  return undefined;
};

interface NotificationDisplayInformation {
  title: string;
  subtitle: string;
  icon: React.ReactNode;
}

const getNotificationDisplayInformation = (
  notification: NotificationListItem,
  user: User,
): NotificationDisplayInformation => {
  const name = notification.actor.contact.name;
  switch (notification.type) {
    /* Conversation Item */
    case NotificationType.CONVERSATION_ITEM_MENTION:
      return {
        title: `${name} mentioned you ${getConversationName(notification.conversationItem.conversation, user)}`,
        subtitle: getMessage(notification),
        icon: <RnAt size="xs" />,
      };
    case NotificationType.CONVERSATION_ITEM_GROUP_MENTION:
      return {
        title: `${name} mentioned your team ${getConversationName(notification.conversationItem.conversation, user)}`,
        subtitle: getMessage(notification),
        icon: <RnAt size="xs" />,
      };
    case NotificationType.CONVERSATION_ITEM_ORGANIZATION_MENTION:
      return {
        title: `${name} mentioned your organization ${getConversationName(
          notification.conversationItem.conversation,
          user,
        )}`,
        subtitle: getMessage(notification),
        icon: <RnAt size="xs" />,
      };
    case NotificationType.CONVERSATION_ITEM_REPLY:
      return {
        title: `${name} replied to a message ${getConversationName(notification.conversationItem.conversation, user)}`,
        subtitle: getMessage(notification),
        icon: <RnReply size="xs" />,
      };
    case NotificationType.CONVERSATION_ITEM_CONVERSATION_SUBSCRIBED:
      return {
        title: `${name} sent a message ${getConversationName(notification.conversationItem.conversation, user)}`,
        subtitle: getMessage(notification),
        icon: <RnMessage size="xs" />,
      };
    case NotificationType.CONVERSATION_ITEM_REACTION: {
      let subtitle = `Message reaction in ${notification.conversationItem.conversation.name}`;
      if (notification.conversationItem.parent?.message) {
        subtitle = stripMarkdown(notification.conversationItem.parent.message.text);
      }
      return {
        title: `${name} reacted to your message ${getConversationName(notification.conversationItem.conversation, user)}`,
        subtitle,
        icon: notification.conversationItem.message?.text ? (
          <Emoji unified={notification.conversationItem.message.text} size={12} />
        ) : null,
      };
    }

    /* Ticket History */
    case NotificationType.TICKET_SUBSCRIBED_STATUS_CHANGE: {
      const TicketStatusIcons: Record<TicketStatus, React.ReactNode> = {
        [TicketStatus.TRIAGE]: <RnTriage size="xs" className="text-icon-orange" />,
        [TicketStatus.OPEN]: <RnStatusNotStarted size="xs" className="text-icon-primary" />,
        [TicketStatus.RESOLVED]: <RnStatusDone size="xs" className="text-icon-success" />,
        [TicketStatus.CANCELED]: <RnCancel size="xs" className="text-icon-tertiary" />,
      };
      let icon: React.ReactNode | null = null;
      if (notification.ticketHistory.field === "status" && notification.ticketHistory.changedTo) {
        icon = TicketStatusIcons[notification.ticketHistory.changedTo];
      }
      return {
        title: `${name} set the status of ${getTicketName(notification.ticketHistory.ticket)}`,
        subtitle: `Ticket set to ${
          notification.ticketHistory.changedTo && notification.ticketHistory.field === TicketHistoryField.status
            ? TicketStatusDisplayNames[notification.ticketHistory.changedTo]
            : "None"
        }`,
        icon,
      };
    }
    case NotificationType.TICKET_SUBSCRIBED_URGENT_PRIORITY_CHANGE:
      return {
        title: `${name} set the priority of ${getTicketName(notification.ticketHistory.ticket)}`,
        subtitle: `Ticket marked as ${
          notification.ticketHistory.changedTo && notification.ticketHistory.field === TicketHistoryField.priority
            ? TicketPriorityDisplayNames[notification.ticketHistory.changedTo]
            : "None"
        }`,
        icon: <RnPriorityUrgent size="xs" className="text-icon-destructive" />,
      };
    case NotificationType.TICKET_SUBSCRIBED_SNOOZED:
      return {
        title: `${name} snoozed ${getTicketName(notification.ticketHistory.ticket)}`,
        subtitle: "Ticket has been snoozed",
        icon: <RnSnooze size="xs" />,
      };
    case NotificationType.TICKET_SUBSCRIBED_URGENT_SNOOZED:
      return {
        title: `${name} snoozed ${getTicketName(notification.ticketHistory.ticket)}`,
        subtitle: "Urgent ticket has been snoozed",
        icon: <RnSnooze size="xs" />,
      };
    case NotificationType.TICKET_ASSIGNED_URGENT_ASSIGNMENT_CHANGE:
      return {
        title: `${name} set assignee of ${getTicketName(notification.ticketHistory.ticket)}`,
        subtitle: "Urgent ticket assignment updated",
        icon: <RnPriorityUrgent size="xs" className="text-icon-destructive" />,
      };
    case NotificationType.TICKET_ASSIGNED_ASSIGNMENT_CHANGE: {
      let subtitle = "Ticket reassigned";
      if (user.person.id === notification.ticketHistory.changedTo) {
        subtitle = "You were assigned the ticket";
      }
      return {
        title: `${name} set assignee of ${getTicketName(notification.ticketHistory.ticket)}`,
        subtitle,
        icon: <RnUserPencil size="xs" />,
      };
    }

    /* Escrow History */
    case NotificationType.ESCROW_STATUS_CHANGE: {
      let subtitle = "Escrow status updated";

      if (notification.escrowHistory.field === "status") {
        const changedFrom = notification.escrowHistory.changedFrom
          ? EscrowStatusDisplayNames[notification.escrowHistory.changedFrom].toLowerCase()
          : "none";
        subtitle = `Changed from ${changedFrom} to ${EscrowStatusDisplayNames[notification.escrowHistory.changedTo].toLowerCase()}`;
      }

      return {
        title: "Escrow status updated",
        subtitle,
        icon: <RnDollarBill size="xs" />,
      };
    }

    /* Financing History */
    case NotificationType.FINANCING_STATUS_CHANGE: {
      let subtitle = "Financing status updated";
      if (notification.financingHistory.field === "status") {
        const changedFrom = notification.financingHistory.changedFrom
          ? FinancingStatusDisplayNames[notification.financingHistory.changedFrom].toLowerCase()
          : "none";
        subtitle = `Changed from ${changedFrom} to ${FinancingStatusDisplayNames[notification.financingHistory.changedTo].toLowerCase()}`;
      }
      return {
        title: "Financing status updated",
        subtitle,
        icon: <RnDollarSign size="xs" />,
      };
    }

    case NotificationType.DEALER_FEE_UNKNOWN: {
      const { financingHistory } = notification;
      return {
        title: "Dealer fee unknown. Pick a plan.",
        subtitle:
          financingHistory.field === "vendor"
            ? `New ${FinancingVendorDisplayNames[financingHistory.changedTo]} loan`
            : "New loan",
        icon: <RnDollarSign size="xs" />,
      };
    }

    /* Pandadoc Document History */
    case NotificationType.HIC_STATUS_CHANGE: {
      let subtitle = "HIC status updated";
      if (notification.pandadocDocumentHistory.field === "status") {
        const changedFrom = notification.pandadocDocumentHistory.changedFrom
          ? PandadocDocumentStatusDisplayNames[notification.pandadocDocumentHistory.changedFrom].toLowerCase()
          : "none";
        subtitle = `Changed from ${changedFrom} to ${PandadocDocumentStatusDisplayNames[notification.pandadocDocumentHistory.changedTo].toLowerCase()}`;
      }
      return {
        title: "HIC status updated",
        subtitle,
        icon: <RnForm size="xs" />,
      };
    }

    case NotificationType.ESCALATION_SUBSCRIBED_STATUS_CHANGE: {
      const stage = parseEscalationStage(notification.escalationHistory.changedTo);
      const fromStage = parseEscalationStage(notification.escalationHistory.changedFrom);
      let title = `${name} set the status`;
      if (stage === "COMPLETION") {
        title = `${name} resolved the escalation`;
      } else if (stage === "DISMISSED") {
        title = `${name} dismissed the escalation`;
      } else if (fromStage && stage && isBackwardTransition(fromStage, stage)) {
        title = `${name} reopened the escalation`;
      }
      return {
        title,
        subtitle: `Escalation set to ${stage ? EscalationStageDisplayNames[stage] : "None"}`,
        icon: stage ? ESCALATION_STAGE_ICONS[stage] : <RnStatusNotStarted size="xs" aria-hidden="true" />,
      };
    }
    case NotificationType.ESCALATION_SUBSCRIBED_RISK_TIER_CHANGE: {
      const tier = parseEscalationRiskTier(notification.escalationHistory.changedTo);
      return {
        title: `${name} changed the risk tier`,
        subtitle: `Escalation set to ${tier ? EscalationRiskTierDisplayNames[tier] : "None"}`,
        icon: tier ? ESCALATION_RISK_TIER_ICONS[tier] : <RnPriorityUrgent size="xs" aria-hidden="true" />,
      };
    }
    case NotificationType.ESCALATION_ASSIGNED_ASSIGNMENT_CHANGE: {
      const { changedFrom, changedTo, changedFromName, changedToName } = notification.escalationHistory;
      const isInitial = !changedFrom;
      const isNewAssignee = user.person.id === changedTo;
      const isOldAssignee = user.person.id === changedFrom;

      const toLabel = isNewAssignee ? "you" : (changedToName ?? "someone");
      const fromLabel = isOldAssignee ? "you" : (changedFromName ?? "someone");

      let title: string;
      if (isInitial) {
        title = `${name} assigned an escalation to ${toLabel}`;
      } else if (isNewAssignee) {
        title = `${name} reassigned an escalation to you`;
      } else if (isOldAssignee) {
        title = `${name} reassigned your escalation`;
      } else {
        title = `${name} reassigned the escalation`;
      }

      const subtitle = isInitial ? `Escalation assigned to ${toLabel}` : `Reassigned from ${fromLabel} to ${toLabel}`;

      return {
        title,
        subtitle,
        icon: <RnUserPencil size="xs" />,
      };
    }

    case NotificationType.NEW_WORK_ORDER:
      return {
        title: "New work order assigned",
        subtitle: "Please accept within 24 hours",
        icon: <RnForm size="xs" />,
      };
  }
};

const ESCALATION_STAGE_ICONS: Record<EscalationStage, React.ReactNode> = {
  NOT_CONTACTED: <RnStatusNotStarted size="xs" className="text-icon-primary" aria-hidden="true" />,
  PENDING_CONTACT: <RnStatusNotStarted size="xs" className="text-icon-primary" aria-hidden="true" />,
  PENDING_RESOLUTION: <RnStatusInProgress size="xs" className="text-icon-teal" aria-hidden="true" />,
  RESOLVED_PENDING_COMPLETION: <RnStatusInProgress size="xs" className="text-icon-teal" aria-hidden="true" />,
  COMPLETION: <RnStatusDone size="xs" className="text-icon-success" aria-hidden="true" />,
  DISMISSED: <RnCancel size="xs" className="text-icon-tertiary" aria-hidden="true" />,
};

const ESCALATION_RISK_TIER_ICONS: Record<EscalationRiskTier, React.ReactNode> = {
  CRITICAL: <RnPriorityUrgent size="xs" aria-hidden="true" />,
  AT_RISK: <RnPriorityHigh size="xs" aria-hidden="true" />,
  WATCH: <RnPriorityMed size="xs" colorClassName1="text-icon-tertiary" aria-hidden="true" />,
  HEALTHY: <RnPriorityLow size="xs" colorClassName1="text-icon-tertiary" aria-hidden="true" />,
};

const parseEscalationStage = (value: string | null): EscalationStage | null => {
  const result = EscalationStageSchema.safeParse(value as unknown);
  return result.success ? result.data : null;
};

const parseEscalationRiskTier = (value: string | null): EscalationRiskTier | null => {
  const result = EscalationRiskTierSchema.safeParse(value as unknown);
  return result.success ? result.data : null;
};

const getMessage = (notification: Extract<NotificationListItem, { type: ConversationItemNotificationType }>) => {
  if (notification.conversationItem.message) {
    return stripMarkdown(notification.conversationItem.message.text);
  }
  if (notification.conversationItem.smsMessage) {
    return stripMarkdown(notification.conversationItem.smsMessage.text);
  }
  return notification.conversationItem.phoneCall.status;
};

const getTimestamp = (notification: NotificationListItem) => {
  return formatDistanceToNowShort(notification.createdAt, { suffix: "ago" });
};
